import React, { useEffect, useRef } from 'react';
import {
  Action,
  AuthType,
  init,
  LiveboardEmbed as TSLiveboardEmbed,
} from '@thoughtspot/visual-embed-sdk';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

type Props = {
  liveboardId: string;
  host: string;
  token: string;
  // ThoughtSpot integer org ID — required in multi-org setups to skip the org selector
  orgId: number;
};

/**
 * Renders a ThoughtSpot liveboard as a full-height iframe.
 * The SDK is initialized once per host/token pair; subsequent renders reuse it.
 */
export default function LiveboardEmbed({ liveboardId, host, token, orgId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the last initialized host so we only re-init when it changes
  const initializedHostRef = useRef<string | null>(null);
  // Always holds the latest token so the getAuthToken closure never goes stale
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!containerRef.current) return;

    if (initializedHostRef.current !== host) {
      init({
        thoughtSpotHost: host,
        authType: AuthType.TrustedAuthTokenCookieless,
        getAuthToken: () => Promise.resolve(tokenRef.current),
        customizations: {
          style: {
            customCSS: {
              rules_UNSTABLE: {
                // Hide the data source/model name chip in the Spotter search bar
                '[data-testid="conv-assist-data-source-selection-btn"]': {
                  display: 'none !important',
                },
              },
            },
          },
          content: {
            strings: {
              Spotter: 'TigerAgent',
            },
          },
          iconSpriteUrl: (() => {
            try {
              const url = new URL('thoughtspot-icons.svg', window.location.href.split('#')[0]);
              return url.protocol === 'https:' ? url.href : undefined;
            } catch {
              return undefined;
            }
          })(),
        },
      });
      initializedHostRef.current = host;
    }

    const embed = new TSLiveboardEmbed(containerRef.current, {
      liveboardId,
      fullHeight: true,
      overrideOrgId: orgId,
      hideLiveboardHeader: false,
      showLiveboardTitle: true,
      hiddenActions: [
        Action.Edit,
        Action.RenameModalTitleDescription,
        Action.ShowUnderlyingData,
        Action.Download,
        Action.CreateMonitor,
        Action.ManageMonitor,
        Action.SpotIQAnalyze,
        Action.Explore,
        Action.SyncToOtherApps,
        Action.SyncToSheets,
        Action.ManagePipelines,
        Action.CopyLink,
        Action.Schedule,
        Action.ExportTML,
        Action.RequestVerification,
        Action.LiveboardInfo,
        Action.SyncToSlack,
        Action.SyncToTeams,
        Action.SchedulesList,
        Action.RequestAccess,
        Action.AIHighlights,
        Action.Share,
        Action.PreviewDataSpotter,
        Action.ChooseDataSources,
      ],
    });

    embed.render();

    // Destroy the embed on cleanup to avoid stale iframes when switching liveboards
    return () => {
      embed.destroy();
    };
  }, [host, liveboardId, orgId]);

  return <div ref={containerRef} className={classes('liveboard-embed')} />;
}
